import { Box, Flex, Text, useMediaQuery } from '@chakra-ui/react';
import cn from 'classnames';
import React, { MouseEventHandler, useEffect, useRef } from 'react';

import { createLog, t, useStateL } from '@/utils';

import styles from './kakao-map.module.scss';

import type { BoxProps, FlexProps } from '@chakra-ui/react';

const log = createLog(`KakaoMap`);

export interface LocationOverlayProps {
  containerProps?: FlexProps;
}

export const LocationOverlay = ({
  // OPTIONAL
  containerProps,
}: LocationOverlayProps) => (
  <Flex
    align="center"
    bottom={{ base: `0`, md: `15%` }}
    justify="flex-start"
    pos="absolute"
    px={16}
    right={{ base: `0`, md: `5%` }}
    zIndex={2}
    sx={{
      backdropFilter: `blur(16px) saturate(180%)`,
      backgroundColor: `rgba(255, 255, 255, 0.75)`,
      borderRadius: { base: 0, md: `20px` },
      border: `1px solid rgba(209, 213, 219, 0.3)`,
    }}
    {...containerProps}
  >
    <Box>
      <Box mb={{ base: 6, md: 12 }}>
        <Text textStyle="content.title-xs">ADDRESS</Text>
        <Text textStyle="content.body-sm">{t(`company.address1`)}</Text>
        <Text textStyle="content.body-sm">{t(`company.address2`)}</Text>
      </Box>
      <Box>
        <Text textStyle="content.title-xs">DIRECTION</Text>
        <Text textStyle="content.body-sm">{t(`company.direction`)}</Text>
      </Box>
    </Box>
  </Flex>
);

export interface KakaoMapProps {
  apiKey: string;
  centerLatLng: number[];
  height: number;

  // OPTIONAL
  borderRadius?: BoxProps['borderRadius'];
  disableMap?: boolean;
  draggable?: boolean;
  markerPositions?: [number, number][];
  width?: number;
  zoomLevel?: number;
  zoomable?: boolean;
}

const KakaoMap = ({
  apiKey,
  centerLatLng,
  height,

  // OPTIONAL
  borderRadius = 0,
  disableMap = false,
  draggable = true,
  markerPositions,
  width,
  zoomLevel: zoomlevel = 3,
  zoomable = false,
}: KakaoMapProps) => {
  const [isMobile] = useMediaQuery(`(max-width: 48em)`);
  const [kakaoMap, setKakaoMap] = useStateL<any>(undefined, `kakaoMap`);
  const [, setMarkers] = useStateL<any[]>([], `markers`);

  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!centerLatLng) return;

    const script = document.createElement(`script`);
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&autoload=false`;
    document.head.appendChild(script);

    script.onload = () => {
      kakao.maps.load(() => {
        const addToLat = isMobile ? -0.004 : 0;
        const addToLng = isMobile ? -0.001 : 0.008;
        const center = new kakao.maps.LatLng(
          centerLatLng[0] + addToLat,
          centerLatLng[1] + addToLng,
        );
        const options = { center, level: zoomlevel };
        const map = new kakao.maps.Map(container.current, options);
        setKakaoMap(map);
      });
    };
  }, [apiKey, centerLatLng, isMobile, setKakaoMap, zoomlevel]);

  useEffect(() => {
    if (kakaoMap && container && container.current) {
      // save center position
      const center = kakaoMap.getCenter();

      // change viewport size
      if (width) container.current.style.width = `${width}px`;
      if (height) container.current.style.height = `${height}px`;

      // relayout and...
      kakaoMap.relayout();

      // restore
      kakaoMap.setCenter(center);
    }
  }, [height, kakaoMap, width]);

  useEffect(() => {
    if (kakaoMap) {
      log.debug(`kakaoMap.setDraggable(${draggable});`);
      kakaoMap.setDraggable(draggable);
    }
  }, [draggable, kakaoMap]);

  useEffect(() => {
    if (kakaoMap) {
      log.debug(`kakaoMap.setZoomable(${zoomable});`);
      kakaoMap.setZoomable(zoomable);
    }
  }, [kakaoMap, zoomable]);

  useEffect(() => {
    if (kakaoMap && markerPositions) {
      const positions = markerPositions.map((pos) => new kakao.maps.LatLng(...pos));

      setMarkers((markers) => {
        // clear prev markers
        markers.forEach((marker) => marker.setMap(null));

        // assign new markers
        return positions.map(
          (pos) => new kakao.maps.Marker({ map: kakaoMap, position: pos }),
        );
      });
    }
  }, [kakaoMap, markerPositions, setMarkers]);

  const handleClickZoomIn: MouseEventHandler = () => {
    if (kakaoMap) {
      kakaoMap.setLevel(kakaoMap.getLevel() - 1);
    }
  };

  const handleClickZoomOut: MouseEventHandler = () => {
    if (kakaoMap) {
      kakaoMap.setLevel(kakaoMap.getLevel() + 1);
    }
  };

  return (
    <Box
      id="container"
      className={styles.container}
      ref={container}
      style={{ position: `relative` }}
      borderRadius={borderRadius}
    >
      <LocationOverlay
        containerProps={{
          w: { base: `100%`, md: `31%` },
          h: { base: `40%`, md: `70%` },
          borderRadius,
        }}
      />
      {disableMap ? (
        <div>KAKAOMAP DISABLED</div>
      ) : (
        <div className={styles.zoomControl}>
          <button
            className={cn(styles.button, styles.zoomIn)}
            onClick={handleClickZoomIn}
            type="button"
          >
            <img
              src="https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/ico_plus.png"
              alt="확대"
            />
          </button>
          <button
            className={cn(styles.button, styles.zoomOut)}
            onClick={handleClickZoomOut}
            type="button"
          >
            <img
              src="https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/ico_minus.png"
              alt="축소"
            />
          </button>
        </div>
      )}
    </Box>
  );
};

export default KakaoMap;
